import React from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronUp, Download, PlugZap } from "lucide-react";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import { Button } from "../../components/misc/Button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../../components/misc/Pagination";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/misc/Select";
import { Input } from "../../components/misc/Input";
import Spinner from "../../components/misc/Spinner";
import { cn } from "../../utils/classMerger";
import PlatformChip from "./PlatformChip";
import {
    SOCIAL_CONNECTION_PLATFORMS,
    SocialConnectionRow,
    SocialConnectionSortColumn,
    SocialConnectionStatusFilter,
    SortDirection,
    formatPlatform,
} from "./types";

const COLUMN_COUNT = 5;

interface SocialConnectionsListProps {
    rows: SocialConnectionRow[];
    loading: boolean;
    exporting: boolean;
    page: number;
    setPage: (page: number) => void;
    limit: number;
    total: number;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    platformFilter: string;
    setPlatformFilter: (platform: string) => void;
    statusFilter: SocialConnectionStatusFilter;
    setStatusFilter: (status: SocialConnectionStatusFilter) => void;
    sortBy?: SocialConnectionSortColumn;
    sortDirection: SortDirection;
    onSortChange: (
        column: SocialConnectionSortColumn,
        direction: SortDirection
    ) => void;
    onExport: () => void;
    isMobile: boolean;
}

const SocialConnectionsList: React.FC<SocialConnectionsListProps> = ({
    rows,
    loading,
    exporting,
    page,
    setPage,
    limit,
    total,
    searchTerm,
    setSearchTerm,
    platformFilter,
    setPlatformFilter,
    statusFilter,
    setStatusFilter,
    sortBy,
    sortDirection,
    onSortChange,
    onExport,
    isMobile,
}) => {
    const totalPages = Math.max(Math.ceil(total / limit), 1);
    const triggerWidth = isMobile ? "w-full" : "w-[180px]";

    const handleSort = (column: SocialConnectionSortColumn): void => {
        const nextDirection: SortDirection =
            sortBy === column && sortDirection === "desc" ? "asc" : "desc";
        onSortChange(column, nextDirection);
        setPage(1);
    };

    const renderSortIcon = (
        column: SocialConnectionSortColumn
    ): React.ReactNode => {
        if (sortBy !== column) return null;
        return sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4 inline ml-1" />
        ) : (
            <ChevronDown className="h-4 w-4 inline ml-1" />
        );
    };

    const sortableHead = (
        label: string,
        column: SocialConnectionSortColumn,
        className?: string
    ): React.ReactNode => (
        <TableHead
            role="button"
            tabIndex={0}
            aria-label={`Sort by ${label}`}
            className={cn(
                "cursor-pointer select-none whitespace-nowrap font-semibold text-primary hover:bg-gray-50",
                className
            )}
            onClick={() => handleSort(column)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSort(column);
                }
            }}
        >
            {label}
            {renderSortIcon(column)}
        </TableHead>
    );

    const emptyCell = <span className="text-gray-400">—</span>;

    return (
        /* min-w-0: as a grid item this Card defaults to min-width:auto, so the
           1100px-wide table would stretch the page instead of scrolling inside. */
        <Card className="mt-0 min-w-0">
            <CardHeader className="p-4 pb-2">
                <div
                    className={cn(
                        "flex gap-3",
                        isMobile
                            ? "flex-col"
                            : "flex-row items-center justify-between"
                    )}
                >
                    <CardTitle className="text-left">
                        {statusFilter === "issues"
                            ? "Users Needing Attention"
                            : "Social Accounts By User"}{" "}
                        ({total})
                    </CardTitle>
                    <Button
                        onClick={onExport}
                        disabled={exporting || loading}
                        className={cn(
                            isMobile && "w-full",
                            exporting && "opacity-50 cursor-not-allowed"
                        )}
                    >
                        <Download className="mr-2 h-4 w-4" />
                        {exporting ? "Exporting..." : "Export CSV"}
                    </Button>
                </div>

                <div
                    className={cn(
                        "flex gap-3 pt-4",
                        isMobile
                            ? "flex-col"
                            : "flex-row flex-wrap items-center"
                    )}
                >
                    <Input
                        type="text"
                        placeholder="Search by name or email"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        className={cn(
                            isMobile ? "w-full" : "min-w-[220px] flex-1"
                        )}
                    />
                    <Select
                        value={statusFilter}
                        onValueChange={(value) => {
                            setStatusFilter(
                                value as SocialConnectionStatusFilter
                            );
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className={triggerWidth}>
                            <SelectValue placeholder="All Users" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Users</SelectItem>
                            <SelectItem value="issues">
                                Needs Attention
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    <Select
                        value={platformFilter}
                        onValueChange={(value) => {
                            setPlatformFilter(value);
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className={triggerWidth}>
                            <SelectValue placeholder="All Platforms" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="All">All Platforms</SelectItem>
                            {SOCIAL_CONNECTION_PLATFORMS.map((platform) => (
                                <SelectItem key={platform} value={platform}>
                                    {formatPlatform(platform)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                </div>

                <p className="pt-2 text-xs text-gray-500">
                    <span className="font-semibold text-primary">Note:</span>{" "}
                    Posts On Social Pause counts only posts paused in the last 3
                    months.
                </p>
            </CardHeader>

            <CardContent className="p-4 pt-0">
                {/* table-fixed + percentage widths: with pixel widths the browser
                    dumps all slack into the last unsized column, leaving a gutter. */}
                <Table className="min-w-[1100px] table-fixed">
                    <TableHeader>
                        <TableRow>
                            {sortableHead("User", "userName", "w-[20%]")}
                            {sortableHead(
                                "Connected",
                                "connectedCount",
                                "w-[32%]"
                            )}
                            {sortableHead(
                                "Disconnected",
                                "disconnectedCount",
                                "w-[20%]"
                            )}
                            {sortableHead(
                                "Last Disconnected",
                                "lastDisconnectedAt",
                                "w-[12%]"
                            )}
                            <TableHead className="w-[16%] whitespace-nowrap font-semibold text-primary">
                                Posts On Social Pause
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={COLUMN_COUNT}
                                    className="py-12"
                                >
                                    <Spinner loadingMessage="Loading social connections..." />
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={COLUMN_COUNT}
                                    className="py-12"
                                >
                                    <div className="flex flex-col items-center gap-3 text-center">
                                        <PlugZap className="h-8 w-8 text-secondary" />
                                        <div>
                                            <p className="font-semibold text-primary">
                                                Everything is connected
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                No disconnected accounts or
                                                paused posts match these
                                                filters.
                                            </p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.userId}>
                                    <TableCell className="align-top">
                                        <div className="min-w-0">
                                            <p className="truncate font-semibold text-primary">
                                                {row.userName || "—"}
                                            </p>
                                            <p className="truncate text-xs text-gray-500">
                                                {row.email}
                                            </p>
                                            {row.phoneNumber && (
                                                <p className="truncate text-xs text-gray-400">
                                                    {row.phoneNumber}
                                                </p>
                                            )}
                                        </div>
                                    </TableCell>

                                    <TableCell className="align-top">
                                        {row.connectedPlatforms.length === 0 ? (
                                            emptyCell
                                        ) : (
                                            // Equal-width grid, not flex-wrap: variable chip
                                            // widths otherwise leave every row ragged.
                                            // The table is a fixed 1100px and scrolls on
                                            // mobile, so two columns still fit there.
                                            <div
                                                className={cn(
                                                    "grid gap-2",
                                                    isMobile
                                                        ? "grid-cols-2"
                                                        : "grid-cols-3"
                                                )}
                                            >
                                                {row.connectedPlatforms.map(
                                                    (platform) => (
                                                        <PlatformChip
                                                            key={platform}
                                                            platform={platform}
                                                            tone="connected"
                                                        />
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </TableCell>

                                    <TableCell className="align-top">
                                        {row.disconnectedAccounts.length ===
                                        0 ? (
                                            emptyCell
                                        ) : (
                                            <div className="grid gap-3">
                                                {row.disconnectedAccounts.map(
                                                    (account) => (
                                                        <div
                                                            key={
                                                                account.platform
                                                            }
                                                        >
                                                            <PlatformChip
                                                                platform={
                                                                    account.platform
                                                                }
                                                                tone="disconnected"
                                                                className="max-w-[190px]"
                                                            />
                                                            <p className="mt-1 truncate pl-1 text-xs text-gray-500">
                                                                {format(
                                                                    new Date(
                                                                        account.disconnectedAt
                                                                    ),
                                                                    "PP"
                                                                )}
                                                                {account.displayName
                                                                    ? ` · ${account.displayName}`
                                                                    : ""}
                                                            </p>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </TableCell>

                                    <TableCell className="whitespace-nowrap align-top">
                                        {row.lastDisconnectedAt ? (
                                            <>
                                                <p className="font-medium text-primary">
                                                    {format(
                                                        new Date(
                                                            row.lastDisconnectedAt
                                                        ),
                                                        "PP"
                                                    )}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {format(
                                                        new Date(
                                                            row.lastDisconnectedAt
                                                        ),
                                                        "p"
                                                    )}
                                                </p>
                                            </>
                                        ) : (
                                            emptyCell
                                        )}
                                    </TableCell>

                                    <TableCell className="align-top">
                                        {row.totalSocialPausePosts === 0 ? (
                                            emptyCell
                                        ) : (
                                            <div className="grid gap-2">
                                                {row.socialPausePosts.map(
                                                    (entry) => (
                                                        <PlatformChip
                                                            key={entry.platform}
                                                            platform={
                                                                entry.platform
                                                            }
                                                            tone="paused"
                                                            className="max-w-[190px]"
                                                            trailing={
                                                                <span className="ml-auto rounded-full bg-primary px-1.5 text-xs font-bold text-white">
                                                                    {
                                                                        entry.count
                                                                    }
                                                                </span>
                                                            }
                                                        />
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>

            {totalPages > 1 && (
                <CardFooter className="p-4 pt-0">
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() =>
                                        setPage(Math.max(page - 1, 1))
                                    }
                                    className={cn(
                                        page === 1 &&
                                            "pointer-events-none opacity-50"
                                    )}
                                />
                            </PaginationItem>
                            {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                    let pageNum: number | null = null;
                                    if (i === 0) pageNum = 1;
                                    else if (i === 4) pageNum = totalPages;
                                    else if (totalPages <= 5) pageNum = i + 1;
                                    else if (page <= 3) pageNum = i + 1;
                                    else if (page >= totalPages - 2)
                                        pageNum = totalPages - 4 + i;
                                    else pageNum = page - 1 + i;
                                    if (!pageNum) {
                                        return (
                                            <PaginationItem key={`e-${i}`}>
                                                <PaginationEllipsis />
                                            </PaginationItem>
                                        );
                                    }
                                    return (
                                        <PaginationItem key={pageNum}>
                                            <PaginationLink
                                                isActive={page === pageNum}
                                                onClick={() => setPage(pageNum!)}
                                            >
                                                {pageNum}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                            )}
                            <PaginationItem>
                                <PaginationNext
                                    onClick={() =>
                                        setPage(Math.min(page + 1, totalPages))
                                    }
                                    className={cn(
                                        page === totalPages &&
                                            "pointer-events-none opacity-50"
                                    )}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </CardFooter>
            )}
        </Card>
    );
};

export default SocialConnectionsList;
